import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, Trash2, Download, ArrowRight, FileText, Compass, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlaybookList() {
  const navigate = useNavigate();
  const [playbooks, setPlaybooks] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pbRes, stRes] = await Promise.all([
        axios.get(`${API}/playbooks`),
        axios.get(`${API}/stores`),
      ]);
      setPlaybooks(pbRes.data);
      setStores(stRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const deletePlaybook = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/playbooks/${id}`);
      toast.success("Playbook deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete playbook");
    }
  };

  const downloadPdf = async (id, title, e) => {
    e.stopPropagation();
    try {
      toast.info("Generating PDF...");
      const res = await axios.get(`${API}/playbooks/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title?.replace(/\s+/g, "_") || "playbook"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (e) {
      toast.error("Failed to generate PDF");
    }
  };

  const getStoreName = (storeId) => {
    if (!storeId) return "Master Template";
    const store = stores.find((s) => s.id === storeId);
    return store ? store.name : "Unknown Store";
  };

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="playbook-list">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-stone-50" />
          </div>
          <span className="text-xs font-medium uppercase tracking-widest text-stone-500">
            Library
          </span>
        </div>
        <h1
          data-testid="playbooks-title"
          className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Your Playbooks
        </h1>
        <p className="text-base text-stone-500 mt-2">
          Generated playbooks from your consulting sessions. View in-app or export as PDF.
        </p>
      </div>

      {/* Business Bible - Primary Playbook */}
      <Card 
        className="mb-8 bg-gradient-to-r from-stone-900 to-stone-800 border-0 rounded-2xl cursor-pointer hover:shadow-xl transition-shadow overflow-hidden"
        onClick={() => navigate("/playbooks/business-bible")}
        data-testid="business-bible-card"
      >
        <CardContent className="p-0">
          <div className="flex items-center">
            <div className="flex-1 p-8">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-amber-400" fill="currentColor" />
                <span className="text-xs font-medium uppercase tracking-widest text-stone-400">The Holy Grail</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Business Bible
              </h2>
              <p className="text-stone-300 text-sm mb-4 max-w-lg">
                The complete guide to who we are, what we do, why we do it, and how we do it. 
                The master document for running every store and ensuring alignment across our entire organization.
              </p>
              <div className="flex items-center gap-4">
                <Button className="bg-white text-stone-900 hover:bg-stone-100">
                  <Compass className="w-4 h-4 mr-2" /> Open Bible
                </Button>
                <span className="text-xs text-stone-400">11 sections • 67 fields</span>
              </div>
            </div>
            <div className="hidden md:flex items-center justify-center w-64 h-full bg-gradient-to-br from-stone-800 to-stone-900 p-8">
              <div className="grid grid-cols-2 gap-2 opacity-30">
                {["Identity", "Purpose", "Product", "Experience", "Operations", "People", "Standards", "Growth"].map(s => (
                  <div key={s} className="px-2 py-1 bg-white/10 rounded text-[10px] text-white text-center">{s}</div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Other Playbooks */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-stone-700 mb-1">Session Playbooks</h2>
        <p className="text-sm text-stone-500">Generated playbooks from your consulting sessions</p>
      </div>

      {/* Playbooks */}
      {playbooks.length === 0 && !loading ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-stone-400" />
          </div>
          <h3
            className="text-lg font-medium text-stone-700 mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            No playbooks yet
          </h3>
          <p className="text-sm text-stone-500 mb-6">
            Chat with your AI consultant and generate a playbook from your conversation.
          </p>
          <Button
            data-testid="go-to-chat-btn"
            onClick={() => navigate("/chat")}
            className="bg-stone-900 text-stone-50 hover:bg-stone-800"
          >
            Start Consulting
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {playbooks.map((pb, idx) => (
            <Card
              key={pb.id}
              data-testid={`playbook-card-${pb.id}`}
              className="card-hover bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl cursor-pointer animate-slide-up"
              style={{ animationDelay: `${idx * 0.08}s` }}
              onClick={() => navigate(`/playbooks/${pb.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${pb.status === "complete" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {pb.status}
                  </span>
                </div>
                <h3
                  className="font-semibold text-stone-900 mb-1 leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {pb.title}
                </h3>
                <p className="text-sm text-stone-500 mb-3">
                  {getStoreName(pb.store_id)} · {pb.sections?.length || 0} sections
                </p>
                <p className="text-xs text-stone-400 mb-4">
                  Created: {pb.created_at?.slice(0, 10)}
                </p>
                <div className="flex gap-2 pt-3 border-t border-stone-100">
                  <Button
                    data-testid={`view-playbook-${pb.id}`}
                    variant="outline"
                    size="sm"
                    className="text-xs border-stone-200 hover:bg-stone-50 flex-1"
                  >
                    <ArrowRight className="w-3 h-3 mr-1" /> View
                  </Button>
                  <Button
                    data-testid={`download-playbook-${pb.id}`}
                    variant="outline"
                    size="sm"
                    onClick={(e) => downloadPdf(pb.id, pb.title, e)}
                    className="text-xs border-stone-200 hover:bg-stone-50"
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button
                    data-testid={`delete-playbook-${pb.id}`}
                    variant="outline"
                    size="sm"
                    onClick={(e) => deletePlaybook(pb.id, e)}
                    className="text-xs border-stone-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
